import { StrictLevelData, StrictPaths, StrictParsedPath, PowerupSpawnSpec, WaterBottleSpawnSpec, EnemySpawnSpec } from '../types/StrictLevelData';
import type { ParsedMapEntities, ParsedPaths } from '../types/MapEntities';

export interface RawLevelData {
  entities: ParsedMapEntities;
  paths: ParsedPaths;
  policy?: { pistolFallback?: boolean };
  map?: { widthInPixels: number; heightInPixels: number };
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function coerceBoolean(v: unknown, fallback = false): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function coercePositiveNumberOrUndefined(v: unknown): number | undefined {
  return isFiniteNumber(v) && v > 0 ? v : undefined;
}

export function normalizeLevelData(raw: RawLevelData): StrictLevelData {
  const warnings: string[] = [];

  // Normalize paths
  const paths: StrictPaths = (() => {
    const byId = new Map<number, StrictParsedPath>();
    const list: StrictParsedPath[] = [];

    const rawList = raw.paths?.list ?? [];
    for (const p of rawList) {
      const id = (p as any).id;
      const closed = coerceBoolean((p as any).closed, false);
      const ptsRaw: any[] = Array.isArray((p as any).points) ? (p as any).points : [];
      const points = ptsRaw
        .map(pt => ({ x: (pt && isFiniteNumber(pt.x)) ? pt.x : NaN, y: (pt && isFiniteNumber(pt.y)) ? pt.y : NaN }))
        .filter(pt => Number.isFinite(pt.x) && Number.isFinite(pt.y));
      if (!isFiniteNumber(id)) {
        warnings.push('Dropped path without finite id');
        continue;
      }
      if (points.length < 2) {
        warnings.push(`Dropped path id=${id} with too few points`);
        continue;
      }
      const sp: StrictParsedPath = { id, closed, points };
      byId.set(id, sp);
      list.push(sp);
    }
    return { byId, list };
  })();

  // Normalize entities
  const powerups: PowerupSpawnSpec[] = [];
  const consumables: WaterBottleSpawnSpec[] = [];
  const enemies: EnemySpawnSpec[] = [];

  // Player spawn (optional)
  let spawn: StrictLevelData['entities']['spawn'] | undefined = undefined;
  const rawSpawn = (raw.entities as any)?.spawn;
  if (rawSpawn && isFiniteNumber(rawSpawn.x) && isFiniteNumber(rawSpawn.y)) {
    spawn = { x: rawSpawn.x, y: rawSpawn.y, facing: ((): any => {
      const f = rawSpawn.facing;
      return f === 'up' || f === 'down' || f === 'left' || f === 'right' ? f : undefined;
    })() };
  } else if (rawSpawn) {
    warnings.push('Dropped invalid player spawn (non-finite coordinates)');
  }

  // Powerups (only Pistol)
  const rawPowerups: any[] = Array.isArray((raw.entities as any)?.powerups) ? (raw.entities as any).powerups : [];
  for (const p of rawPowerups) {
    if ((p?.type) !== 'Pistol') {
      warnings.push(`Unsupported powerup type dropped: ${String(p?.type)}`);
      continue;
    }
    if (!isFiniteNumber(p.x) || !isFiniteNumber(p.y)) {
      warnings.push('Dropped powerup with non-finite coordinates');
      continue;
    }
    const ammo = isFiniteNumber(p.ammo) ? p.ammo : 12;
    const respawn = coercePositiveNumberOrUndefined(p.respawn);
    const rotate = coerceBoolean(p.rotate, false);
    powerups.push({
      kind: 'Powerup',
      type: 'Pistol',
      x: p.x,
      y: p.y,
      ammo,
      respawn,
      rotate,
    });
  }

  // Consumables (WaterBottle)
  const rawConsumables: any[] = Array.isArray((raw.entities as any)?.consumables) ? (raw.entities as any).consumables : [];
  for (const c of rawConsumables) {
    if ((c?.type) !== 'WaterBottle') {
      warnings.push(`Unsupported consumable type dropped: ${String(c?.type)}`);
      continue;
    }
    if (!isFiniteNumber(c.x) || !isFiniteNumber(c.y)) {
      warnings.push('Dropped water bottle with non-finite coordinates');
      continue;
    }
    const heal = isFiniteNumber(c.heal) ? c.heal : 25;
    const respawn = coercePositiveNumberOrUndefined(c.respawn);
    const rotate = coerceBoolean(c.rotate, false);
    consumables.push({
      kind: 'Consumable',
      type: 'WaterBottle',
      x: c.x,
      y: c.y,
      heal,
      respawn,
      rotate,
    });
  }

  // Enemies (support known types only)
  const rawEnemies: any[] = Array.isArray((raw.entities as any)?.enemies) ? (raw.entities as any).enemies : [];
  for (const e of rawEnemies) {
    const type = e?.type;
    if (type !== 'Grunt' && type !== 'Fast' && type !== 'Tank') {
      warnings.push(`Unsupported enemy type dropped: ${String(type)}`);
      continue;
    }
    if (!isFiniteNumber(e.x) || !isFiniteNumber(e.y)) {
      warnings.push('Dropped enemy with non-finite coordinates');
      continue;
    }
    let patrolPathId: number | undefined = undefined;
    if (isFiniteNumber(e.patrolPathId)) {
      if (paths.byId.has(e.patrolPathId)) {
        patrolPathId = e.patrolPathId;
      } else {
        warnings.push(`Enemy referenced missing patrolPathId=${e.patrolPathId}; removed reference`);
      }
    }
    const patrolLoop = coerceBoolean(e.patrolLoop, false);
    const patrolWait = isFiniteNumber(e.patrolWait) && e.patrolWait >= 0 ? e.patrolWait : undefined;
    const speed = isFiniteNumber(e.speed) ? e.speed : undefined;
    const vision = isFiniteNumber(e.vision) ? e.vision : undefined;

    enemies.push({
      kind: 'Enemy',
      type,
      x: e.x,
      y: e.y,
      speed,
      vision,
      patrolPathId,
      patrolLoop,
      patrolWait,
    });
  }

  // Optional policy: pistol fallback injection
  if (raw.policy?.pistolFallback && powerups.length === 0 && raw.map && isFiniteNumber(raw.map.widthInPixels) && isFiniteNumber(raw.map.heightInPixels)) {
    const w = raw.map.widthInPixels;
    const h = raw.map.heightInPixels;
    const corners = [
      { x: 32, y: 32 },
      { x: w - 32, y: 32 },
      { x: 32, y: h - 32 },
      { x: w - 32, y: h - 32 },
    ];
    for (const pt of corners) {
      powerups.push({
        kind: 'Powerup',
        type: 'Pistol',
        x: pt.x,
        y: pt.y,
        ammo: 12,
        respawn: undefined,
        rotate: false,
      });
    }
    warnings.push('Injected pistolFallback: spawned four corner pistols due to empty powerups');
  } else if (raw.policy?.pistolFallback && powerups.length === 0) {
    warnings.push('pistolFallback requested but map size missing; no fallback applied');
  }

  return {
    entities: {
      spawn,
      powerups,
      consumables,
      enemies,
    },
    paths,
    warnings,
  };
}